/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { StaggeredEntrance } from '@atlaskit/motion';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import type { FlagsContainer } from '../audio-player/AudioContainer';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistItem, type PlaylistItemData } from './PlaylistItem';

const messages = defineMessages({
	nowPlaying: {
		id: 'ai-audio.playlist.now-playing',
		defaultMessage: 'Now playing',
		description: 'Header for the currently playing item section in the playlist',
	},
	upNext: {
		id: 'ai-audio.playlist.up-next',
		defaultMessage: 'Up next',
		description: 'Header for the upcoming items section in the playlist',
	},
} as const);

const styles = cssMap({
	playlist: {
		flex: 1,
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	sectionHeader: {
		textAlign: 'left',
		paddingLeft: token('space.100'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.050'),
		color: token('color.text.subtle'),
	},
	sectionList: {
		listStyleType: 'none',
		margin: '0',
		padding: '0',
	},
	upNextSection: {
		paddingTop: token('space.150'),
	},
	player: {
		marginLeft: token('space.050'),
		marginRight: token('space.050'),
	},
	emptySection: {
		paddingTop: token('space.200'),
	},
});

interface PlaylistViewProps {
	currentItem: PlaylistItemData | null;
	items: PlaylistItemData[];
	onItemClick: (item: PlaylistItemData) => void;
	flags: FlagsContainer;
}

export const PlaylistView = ({ items, currentItem, onItemClick, flags }: PlaylistViewProps) => {
	const { formatMessage } = useIntl();

	// Separate currently playing item from the rest
	const remainingItems = currentItem ? items.filter((item) => item.id !== currentItem.id) : items;

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	const renderPlaylistItem = useCallback(
		(item: PlaylistItemData, index: number, totalCount: number) => (
			<li key={item.id}>
				<PlaylistItem
					data={item}
					onItemClick={onItemClick}
					isFirstItem={currentItem ? index === 1 : index === 0}
					isLastItem={index === totalCount - 1}
					flags={flags}
					currentIndex={index}
					allItems={items}
				/>
			</li>
		),
		[onItemClick, currentItem, flags, items],
	);

	return (
		<StaggeredEntrance columns={1}>
			<Flex xcss={styles.playlist} direction="column">
				{currentItem && !isUninitialized ? (
					<>
						{/* Now Playing Section */}
						<Box xcss={styles.sectionHeader}>
							<Heading as="h3" size="xxsmall">
								{formatMessage(messages.nowPlaying)}
							</Heading>
						</Box>
						<Stack as="ul" space="space.100" xcss={styles.sectionList}>
							<li key={currentItem.id} css={styles.player}>
								<MinifiedAudioPlayer
									title={currentItem.title}
									byline=""
									avatarUrl={currentItem.avatarUrl || ''}
									elevated
								/>
							</li>
						</Stack>

						{/* Up Next Section */}
						<Box xcss={styles.upNextSection}>
							<Box xcss={styles.sectionHeader}>
								<Heading as="h3" size="xxsmall">
									{formatMessage(messages.upNext)}
								</Heading>
							</Box>
							{remainingItems.length > 0 ? (
								<Stack as="ul" space="space.100" xcss={styles.sectionList}>
									{remainingItems.map((item, index) => {
										return renderPlaylistItem(item, index + 1, items.length);
									})}
								</Stack>
							) : (
								<Flex xcss={styles.emptySection}>
									<PlaylistEmptyView />
								</Flex>
							)}
						</Box>
					</>
				) : (
					/* Nothing is playing, show all items */
					<Stack as="ul" space="space.100" xcss={styles.sectionList}>
						{items.map((item, index) => renderPlaylistItem(item, index, items.length))}
					</Stack>
				)}
			</Flex>
		</StaggeredEntrance>
	);
};
